/**
 * Onboarding Compound Component
 *
 * Combines all primitives into a single compound component using Object.assign.
 * Similar to the Form pattern in admin-console.
 *
 * @example
 * import { Onboarding } from './primitives';
 *
 * <Onboarding.Dialog open={open} dismissible={false}>
 *   <Onboarding.StepIndicator current={2} total={4} />
 *   <Onboarding.Header>
 *     <Onboarding.Icon><Shield /></Onboarding.Icon>
 *     <Onboarding.Title>Set Up Password</Onboarding.Title>
 *     <Onboarding.Description>Create a secure password</Onboarding.Description>
 *   </Onboarding.Header>
 *   <Onboarding.Body>
 *     <Onboarding.Error error={error} onDismiss={clearError} />
 *     {content}
 *   </Onboarding.Body>
 *   <Onboarding.Footer>
 *     <Onboarding.BackButton onClick={onBack}>Cancel</Onboarding.BackButton>
 *     <Onboarding.NextButton loading={isLoading}>Continue</Onboarding.NextButton>
 *   </Onboarding.Footer>
 * </Onboarding.Dialog>
 */

import {
  OnboardingBackButton,
  OnboardingNextButton,
  OnboardingSkipButton,
} from './onboarding-buttons';
import { OnboardingDialog } from './onboarding-dialog';
import {
  OnboardingBody,
  OnboardingContent,
  OnboardingDescription,
  OnboardingFooter,
  OnboardingHeader,
  OnboardingIcon,
  OnboardingTitle,
} from './onboarding-primitives';
import { OnboardingError, OnboardingLoading } from './onboarding-states';
import { OnboardingStepIndicator } from './onboarding-step-indicator';

/**
 * Onboarding Compound Component
 *
 * Provides a consistent API for building onboarding flows:
 * - Onboarding.Dialog - Modal wrapper with dismissible option
 * - Onboarding.StepIndicator - Progress dots
 * - Onboarding.Content - Content wrapper
 * - Onboarding.Header - Header container
 * - Onboarding.Icon - Icon with background
 * - Onboarding.Title - Title typography
 * - Onboarding.Description - Subtitle text
 * - Onboarding.Body - Main content area
 * - Onboarding.Footer - Action buttons container
 * - Onboarding.NextButton - Primary action
 * - Onboarding.BackButton - Secondary action
 * - Onboarding.SkipButton - Ghost action
 * - Onboarding.Error - Error alert
 * - Onboarding.Loading - Loading spinner
 */
export const Onboarding = {
  // Dialog
  Dialog: OnboardingDialog,

  // Progress
  StepIndicator: OnboardingStepIndicator,

  // Structure
  Content: OnboardingContent,
  Header: OnboardingHeader,
  Body: OnboardingBody,
  Footer: OnboardingFooter,

  // Typography
  Icon: OnboardingIcon,
  Title: OnboardingTitle,
  Description: OnboardingDescription,

  // Buttons
  NextButton: OnboardingNextButton,
  BackButton: OnboardingBackButton,
  SkipButton: OnboardingSkipButton,

  // States
  Error: OnboardingError,
  Loading: OnboardingLoading,
};
